import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import stSeat from 'assets/images/icons/st_seat.svg'
import vipSeat from 'assets/images/icons/vip_seat.svg'
import backgroundImage from 'assets/images/boat_background.svg'
import { CheckCircleIcon, LockClosedIcon, TicketIcon } from '@heroicons/react/24/outline'

const columns = ['A', 'B', 'C', 'D']

/**
 * Standard Seat Blocking Layout Component
 * Handles standard vessel layout with VIP and STD sections for seat blocking/unblocking
 * Supports multi-select for blocking or releasing seats
 */
export function SeatBlockingLayout({
    seatLayout,
    selectedSeats,
    onSeatToggle,
    seatLayoutKey,
    availableSeats,
}) {
    const { t } = useTranslation()

    // Get seat status from available seats data
    const getSeatStatus = (seatNo) => {
        const seat = availableSeats.find(s => s.seat_no === seatNo)
        return seat?.seat_status || null
    }

    // Check if seat is available for selection based on current selection type
    const isSeatSelectable = (seatNo, seatStatus) => {
        if (seatStatus === 'BKD' || seatStatus === 'BOOKED') return false
        if (selectedSeats.length === 0) return true
        
        // Get the status of the first selected seat
        const firstSelectedSeatStatus = getSeatStatus(selectedSeats[0])
        
        // Normalize statuses for comparison (treat null/AVL as same, BLK as BLK)
        const normalizeStatus = (s) => s === 'BLK' ? 'BLK' : 'AVL'
        
        // Only allow selecting seats of the same status type
        return normalizeStatus(seatStatus) === normalizeStatus(firstSelectedSeatStatus)
    }

    /**
     * Render individual seat (VIP or STD)
     */
    const renderSeat = (seat, seatType) => {
        const seatKey = `${seat.row}${seat.column}`
        const seatStatus = getSeatStatus(seatKey)
        const isSelected = selectedSeats.includes(seatKey)
        const isBooked = seatStatus === 'BKD' || seatStatus === 'BOOKED'
        const isBlocked = seatStatus === 'BLK'
        const isAvailable = seatStatus === 'AVL'
        const isVIP = seatType === 'VIP'
        const canSelect = (seat.available || isBlocked) && isSeatSelectable(seatKey, seatStatus)

        // Determine seat styling - matching booking screen style
        let seatClasses = 'relative flex items-center justify-center rounded-md cursor-pointer h-[30px] w-[30px] transition-all duration-200 '
        
        if ((!seat.available && !isBlocked) || isBooked) {
            seatClasses += 'bg-[#e0e8e9] border-[0px] text-gray-400 cursor-not-allowed'
        } else if (isSelected) {
            seatClasses += 'bg-[#128472] border-[#128472] text-white shadow-lg'
        } else if (!canSelect && selectedSeats.length > 0) {
            seatClasses += 'bg-[#e0e8e9] border-[0px] text-gray-400 cursor-not-allowed opacity-50'
        } else if (isBlocked) {
            seatClasses += 'bg-orange-100 border-[1px] border-orange-400 text-orange-700 shadow-sm hover:shadow-md'
        } else if (isVIP) {
            seatClasses += 'bg-white border-[1px] border-[#e3bf02] text-[#9b7d00] shadow-sm hover:shadow-md'
        } else {
            seatClasses += 'bg-white border-[1px] border-teal-400 text-teal-800 shadow-sm hover:shadow-md'
        }

        return (
            <div className="flex justify-center items-center">
                <div
                    className={seatClasses}
                    onClick={() => {
                        if ((seat.available || isBlocked) && canSelect) {
                            onSeatToggle(seatKey, seatStatus, seat.available)
                        }
                    }}
                >
                    <span className="text-sm font-bold">{seat.row}</span>
                    
                    {/* Status badge */}
                    {isBooked && (
                        <LockClosedIcon className="absolute -top-1 -right-1 h-3 w-3 text-red-600 bg-white rounded-full p-0.5" />
                    )}
                    {isBlocked && !isSelected && (
                        <TicketIcon className="absolute -top-1 -right-1 h-3 w-3 text-orange-600 bg-white rounded-full p-0.5" />
                    )}
                    {isSelected && (
                        <CheckCircleIcon className="absolute -top-1 -right-1 h-3.5 w-3.5 text-white bg-[#128472] rounded-full" />
                    )}
                </div>
            </div>
        )
    }

    // Check if VIP section should be shown
    const showVIPSection = seatLayoutKey === 'standard_vessels' && seatLayout.VIP.length > 0

    return (
        <div className="w-full relative h-auto">
            <div
                className="relative border-solid border-[#e0e8e9] md:pt-[40%] xl:pt-[45%]"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'bottom',
                    paddingInlineStart: '20px',
                    paddingInlineEnd: '20px',
                    paddingBottom: '10px',
                }}
            >
                {/* VIP Seats Section - Only show for standard_vessels layout */}
                {showVIPSection && (
                    <>
                        <div className="flex space-x-5 justify-center w-full px-4">
                            {/* VIP Seats Label */}
                            <div className="flex w-full text-start text-[14px] font-semibold text-primary mb-2 ps-6  border-solid border-gray-200 pb-2">
                                <img
                                    src={vipSeat}
                                    className="w-[20px] me-2"
                                    alt="VIP"
                                />
                                {t('VIP Seats')}
                            </div>
                        </div>
                        <div className="flex space-x-5 justify-center w-full px-4">
                            {columns.map((column, colIndex) => (
                                <div className="flex-1" key={colIndex}>
                                    {/* Column Label */}
                                    <div className="text-center font-semibold text-primary mb-2">
                                        {column}
                                    </div>

                                    {/* VIP Seats */}
                                    <div className="space-y-2">
                                        {seatLayout.VIP.filter(
                                            seat => seat.column === column
                                        ).map((seat, index) => (
                                            <div key={index}>
                                                {renderSeat(seat, 'VIP')}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}  
                        </div>
                    </>
                )}

                {/* STD Seats Section */}
                <div className="flex space-x-5 justify-center w-full mt-4 px-4">
                    {/* STD Seats Label */}
                    <div className="flex w-full text-start text-[14px] font-semibold text-primary mb-2 ps-6  border-solid border-gray-200 pb-2">
                        <img
                            src={stSeat}
                            className="w-[20px] me-2"
                            alt="STD"
                        />
                        {t('Standard Seats')}
                    </div>
                </div>
                <div className="flex space-x-5 justify-center w-full px-4">
                    {columns.map((column, colIndex) => (
                        <div className="flex-1" key={colIndex}>
                            {/* Column Label */}
                            <div className="text-center font-semibold text-primary mb-2">
                                {column}
                            </div>

                            {/* STD Seats */}
                            <div className="space-y-2">
                                {seatLayout.STD.filter(
                                    seat => seat.column === column
                                ).map((seat, index) => (
                                    <div key={index}>
                                        {renderSeat(seat, 'STD')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

SeatBlockingLayout.propTypes = {
    seatLayout: PropTypes.object.isRequired,
    selectedSeats: PropTypes.array.isRequired,
    onSeatToggle: PropTypes.func.isRequired,
    seatLayoutKey: PropTypes.string,
    availableSeats: PropTypes.array.isRequired,
}
